import {
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal
} from '@angular/core';
import {
  RecordData,
  RecordTableColumnParams,
  RecordUpdateRequestParams,
  SelectData
} from "../../../../../../types/records-table.types";
import {BloggerCardProfile, BloggerProfileDialogData} from "../../../types/blogger-modal.types";
import {defaultRecordData} from "../../../../../../models/default-record-data";
import {BloggerModalService} from "../../../services/blogger-modal.service";
import {bloggerProfileModalName, currentBloggerSelectFormFields} from "../../../configs/blogger-card-modal-config";
import {HandleSelectChangeParams} from "../../../types/current-blogger-card-modal.types";
import {bloggerModalResourceType, skipUnbindCheckPaths} from "../../../configs/blogger-modal-config";
import {defaultRecordPropertyData} from "../../../../../../models/default-record-property-data";
import {RecordTableAction} from "../../../../../../types/record-table-action.types";
import {HeaderItem} from "../../../types/blogger-card.types";
import {CurrentBloggerProfileService} from "../../../services/current-blogger-profile.service";
import {MatDialog} from "@angular/material/dialog";
import {BaseConfirmModalComponent} from "../../../../base-confirm-modal/base-confirm-modal.component";
import {deleteConfirmationParams} from "../../../configs/current-blogger-card-modal-config";
import {DetachProps} from "../../../../../../types/attach-detach.types";
import {ImgFallbackDirective} from "../../../../../../../shared/directives/img-fallback.directive";
import { MatAnchor, MatButton, MatIconButton, MatMiniFabButton } from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatFormField, MatLabel} from "@angular/material/input";
import {MatOption, MatSelect} from "@angular/material/select";
import {ActionDropdownMenuComponent} from "../../../../../action-dropdown-menu/action-dropdown-menu.component";
import {BloggerProfileTablesComponent} from "../blogger-profile-tables/blogger-profile-tables.component";
import {ModalStatesService} from "../../../../../../services/table-services/modal-states.service";
import {ModalsService} from "../../../../../../services/modals.service";
import {UtilityService} from "../../../../../../services/utility.service";
import { MatTooltip } from "@angular/material/tooltip";

@Component({
  selector: 'app-current-blogger-profile-modal',
  standalone: true,
  imports: [
    ImgFallbackDirective,
    MatMiniFabButton,
    MatIcon,
    MatButton,
    MatAnchor,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    ActionDropdownMenuComponent,
    BloggerProfileTablesComponent,
    MatIconButton,
    MatTooltip
  ],
  templateUrl: './current-blogger-profile-modal.component.html',
  styleUrl: './current-blogger-profile-modal.component.scss'
})
export class CurrentBloggerProfileModalComponent implements OnInit, OnDestroy {
  bloggerCardHeader = input.required<RecordData>();
  bloggerDialogData = input.required<BloggerProfileDialogData>();
  bloggerPropertiesParams = input<Array<RecordTableColumnParams>>([]);
  initBloggerCardHeader = output<RecordData | null>();

  private bloggerModalService: BloggerModalService = inject(BloggerModalService);
  private currentBloggerProfileService: CurrentBloggerProfileService = inject(CurrentBloggerProfileService);
  private modalStatesService: ModalStatesService = inject(ModalStatesService);
  private modalService: ModalsService = inject(ModalsService);
  private dialog: MatDialog = inject(MatDialog);
  private utilityService: UtilityService = inject(UtilityService);

  public readonly currentBloggerSelectFormFields = currentBloggerSelectFormFields;
  public profiles = signal<Array<BloggerCardProfile>>([]);
  public canBloggerUnbind = signal<boolean>(false);
  public actions = signal<Array<RecordTableAction>>([]);
  public isBlacklistBlogger = signal<boolean>(false);
  public bloggerId = computed(() => this.bloggerDialogData().bloggerId || 0);
  public blacklistText = computed(() => this.isBlacklistBlogger() ? 'В чёрном списке' : 'Не в чёрном списке');
  public blacklistTooltipText = computed(() => this.isBlacklistBlogger() ? 'Удалить из чёрного списка' : 'Добавить в чёрный спискок');

  public async ngOnInit(): Promise<void> {
    await this.updateBloggerCardData();
    this.actions.set(this.getActionList());
    await this.checkIfBloggerCanBeUnbound();
  }

  public ngOnDestroy(): void {
    this.modalStatesService.closeModal(bloggerProfileModalName);
  }

  public get headerItems(): Array<HeaderItem> {
    return this.currentBloggerProfileService.headerItems(this.bloggerCardHeader());
  }

  public async updateRating(): Promise<void> {
    await this.currentBloggerProfileService.updateRating(Number(this.bloggerCardHeader()?.id));
    await this.updateBloggerCardData();
  }

  public async checkIfBloggerCanBeUnbound() {
    if (skipUnbindCheckPaths.includes(this.bloggerDialogData().parentResourcePath)) { return; }

    await this.currentBloggerProfileService.checkIfBloggerCanBeUnbound(this.bloggerDialogData())
      .then((data) => this.canBloggerUnbind.set(data));
  }

  public async handleButtonClick(key: string): Promise<void> {
    await this.currentBloggerProfileService.handleButtonClick(key, this.bloggerDialogData());
  }

  public async handleAudienceButtonClick(): Promise<void> {
    const { recordData } = this.bloggerDialogData();
    await this.modalService.getStatusFromAudienceModal('audienceId', recordData);
  }

  public getSelectedDataColor(key: string): string | null {
    const value = this.bloggerCardHeader()?.[key];
    if (!value) { return null; }

    const selectedData = this.getSelectData(key)?.find((selectData: SelectData) =>
      selectData.id.toString() == value
    ) ?? null;

    if (!selectedData || !selectedData.color) { return null; }

    return this.utilityService.hexToRgba(selectedData.color, 0.35);
  }

  public handleSelectChange({ event, key, matSelect}: HandleSelectChangeParams): void  {
    if (!this.bloggerId()) { return; }

    const recordUpdateRequestParams: RecordUpdateRequestParams = {
      path: bloggerModalResourceType.path,
      recordId: this.bloggerId(),
      fieldName: key,
      value: event.value
    }

    this.bloggerModalService.updatedRecordField<RecordData>(recordUpdateRequestParams)
      .catch(() => defaultRecordPropertyData);
    matSelect.close();
  }

  public getOptions(key: string): Array<SelectData> | null | undefined {
    return this.getSelectData(key);
  }

  public getColorWithOpacity(hex: string | null, opacity: number): string {
    if (!hex) { return '#fff'; }

    return this.utilityService.hexToRgba(hex, opacity);
  }

  public getActionList(): Array<RecordTableAction> {
    return this.currentBloggerProfileService.getActionList(this.canBloggerUnbind(), this.bloggerCardHeader());
  }

  public handleActionSelect(action: RecordTableAction) {
    switch (action) {
      case 'UNBIND_BLOGGER':
        this.openConfirmationDialog();
        return;
      case 'EDIT_BLOGGER':
      case 'EDIT_PROFILES_BLOGGER':
      case 'CREATE_PROFILE_BLOGGER':
      case 'DELETE_PROFILE_BLOGGER':
        this.openEdit(this.bloggerCardHeader(), action);
        return;
    }
  }

  public async handelToggleBlacklist(): Promise<void> {
    const path = this.isBlacklistBlogger() ?
      `remove/${this.bloggerId()}` :
      `add/${this.bloggerId()}`;
    await this.bloggerModalService.toggleBlacklistBlogger(path)
      .then(() => this.isBlacklistBlogger.update(i => !i));
  }

  private async updateBloggerCardData(): Promise<void> {
    if (!this.bloggerId()) { return; }

    const bloggerData: RecordData = await this.bloggerModalService.getBloggerDataById(this.bloggerId())
      .catch(() => defaultRecordData);
    this.profiles.set(bloggerData?.['profiles'] ? bloggerData['profiles'] as Array<BloggerCardProfile> : []);
    this.isBlacklistBlogger.set(!!bloggerData['blacklisted']);
    this.initBloggerCardHeader.emit(bloggerData);
  }

  private getSelectData(key: string): Array<SelectData> | null | undefined {
    return this.bloggerPropertiesParams().find(i => i.key === key)?.metaSelectKeyList;
  }

  private openConfirmationDialog(): void {
    this.dialog.open(BaseConfirmModalComponent, {
      data: {
        deleteConfirmationParams: deleteConfirmationParams
      }
    }).afterClosed().subscribe((result) => {
      if (result === 'remove') {
        this.unbindContact().then();
      }
    });
  }

  private openEdit(recordData?: RecordData | null, action?: RecordTableAction): void {
    if (!recordData || !action) { return; }

    const modal = this.currentBloggerProfileService.getModalType(action);
    this.dialog.open(modal, {
      data: {
        recordData,
        bloggerPropertiesParams: this.bloggerPropertiesParams()
      }
    }).afterClosed().subscribe(() => {
      this.updateBloggerCardData().then();
    });
  }

  private async unbindContact(): Promise<void> {
    const detachProps: DetachProps = this.currentBloggerProfileService.getDetachProps(this.bloggerDialogData());

    await this.bloggerModalService.detachRecordData(detachProps)
      .then(() => this.initBloggerCardHeader.emit(null));
  }
}
